import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    AlertTriangle,
    CalendarDays,
    CheckCircle,
    ChevronDown,
    ChevronRight,
    Clock,
    Flag,
    HelpCircle,
    Loader2,
    Trophy,
} from 'lucide-react';
import {
    api,
    MilestoneBrowseResponse,
    MilestoneBrowseRow,
    MilestoneBucket,
    MilestoneSummaryResponse,
} from '../../lib/api';

// Bucket order drives chip placement and list sort. Mirrors the
// ORDER BY CASE used in backend/routes/admin_milestones.js.
const BUCKET_ORDER: MilestoneBucket[] = [
    'overdue',
    'in_progress',
    'due_soon',
    'upcoming',
    'achieved',
    'unscheduled',
];

const BUCKET_META: Record<MilestoneBucket, {
    label: string;
    icon: React.ComponentType<{ className?: string }>;
    pillClass: string;    // text + bg
    chipClass: string;    // chip when unselected
    chipActive: string;   // chip when selected
}> = {
    overdue: {
        label: 'Overdue',
        icon: AlertTriangle,
        pillClass: 'bg-red-100 text-red-700 border-red-200',
        chipClass: 'border-red-200 text-red-700 bg-white hover:bg-red-50',
        chipActive: 'bg-red-600 text-white border-red-600 shadow-sm',
    },
    in_progress: {
        label: 'In progress',
        icon: Clock,
        pillClass: 'bg-blue-100 text-blue-700 border-blue-200',
        chipClass: 'border-blue-200 text-blue-700 bg-white hover:bg-blue-50',
        chipActive: 'bg-blue-600 text-white border-blue-600 shadow-sm',
    },
    due_soon: {
        label: 'Due soon',
        icon: Flag,
        pillClass: 'bg-amber-100 text-amber-700 border-amber-200',
        chipClass: 'border-amber-200 text-amber-700 bg-white hover:bg-amber-50',
        chipActive: 'bg-amber-600 text-white border-amber-600 shadow-sm',
    },
    upcoming: {
        label: 'Upcoming',
        icon: CalendarDays,
        pillClass: 'bg-slate-100 text-slate-700 border-slate-200',
        chipClass: 'border-slate-200 text-slate-700 bg-white hover:bg-slate-50',
        chipActive: 'bg-slate-700 text-white border-slate-700 shadow-sm',
    },
    achieved: {
        label: 'Achieved',
        icon: Trophy,
        pillClass: 'bg-emerald-100 text-emerald-700 border-emerald-200',
        chipClass: 'border-emerald-200 text-emerald-700 bg-white hover:bg-emerald-50',
        chipActive: 'bg-emerald-600 text-white border-emerald-600 shadow-sm',
    },
    unscheduled: {
        label: 'Unscheduled',
        icon: HelpCircle,
        pillClass: 'bg-zinc-100 text-zinc-600 border-zinc-200',
        chipClass: 'border-zinc-200 text-zinc-600 bg-white hover:bg-zinc-50',
        chipActive: 'bg-zinc-600 text-white border-zinc-600 shadow-sm',
    },
};

function todayOffset(days: number): string {
    const d = new Date();
    d.setDate(d.getDate() + days);
    return d.toISOString().slice(0, 10);
}

function formatDate(iso?: string | null) {
    if (!iso) return '—';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '—';
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

export interface MilestonesBrowsePanelProps {
    projectId: string;
    // Subtree scope: only milestones under this milestone are fetched.
    // Pass null for project-wide scoping.
    parentMilestoneId?: string | null;
    // Location scope: only milestones whose leaves belong to this building.
    // Useful when the host view already has a building picker active.
    buildingId?: string | null;
    // Human label for the current scope, shown above the filter section so
    // the user knows the panel isn't showing the whole project.
    scopeLabel?: string | null;
    // Fired on every check/uncheck. Carries the full payload so the host
    // can auto-populate the task table without waiting for an explicit
    // "Load" click. Host is responsible for debouncing if needed.
    onSelectionChange: (payload: {
        milestoneIds: string[];
        leafTaskIds: string[];
        milestoneRows: MilestoneBrowseRow[];
    }) => void;
}

const PAGE_SIZE = 200;

export default function MilestonesBrowsePanel({
    projectId,
    parentMilestoneId,
    buildingId,
    scopeLabel,
    onSelectionChange,
}: MilestonesBrowsePanelProps) {
    const [from, setFrom] = useState(() => todayOffset(-30));
    const [to, setTo]     = useState(() => todayOffset(90));
    // Which buckets the user wants to see. Unscheduled is excluded by default
    // because the pool is huge (~4k on Harita) and overwhelms the list.
    const [activeBuckets, setActiveBuckets] = useState<Set<MilestoneBucket>>(
        () => new Set<MilestoneBucket>(['overdue', 'in_progress', 'due_soon', 'upcoming'])
    );

    const [summary, setSummary] = useState<MilestoneSummaryResponse | null>(null);
    const [rows, setRows] = useState<MilestoneBrowseRow[]>([]);
    const [total, setTotal] = useState(0);
    const [offset, setOffset] = useState(0);
    const [loading, setLoading] = useState(false);
    const [loadingMore, setLoadingMore] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const [checkedIds, setCheckedIds] = useState<Set<string>>(new Set());
    // Group visibility uses an *expanded* set rather than collapsed — that
    // way a fresh fetch defaults to every group collapsed (set empty).
    const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());

    // Reset selection/expansion state when scope or project changes.
    useEffect(() => {
        setCheckedIds(new Set());
        setExpandedGroups(new Set());
    }, [projectId, parentMilestoneId, buildingId]);

    const fetchSummary = useCallback(async () => {
        if (!projectId) return;
        try {
            const s = await api.adminMilestoneSummary(projectId, {
                from, to,
                parentId: parentMilestoneId || undefined,
                buildingId: buildingId || undefined,
            });
            setSummary(s);
        } catch (err: any) {
            console.error('[milestones] summary failed:', err);
        }
    }, [projectId, from, to, parentMilestoneId, buildingId]);

    const fetchList = useCallback(async (nextOffset = 0, append = false) => {
        if (!projectId) return;
        try {
            if (append) setLoadingMore(true); else { setLoading(true); setError(null); }
            const res: MilestoneBrowseResponse = await api.adminListMilestones(projectId, {
                from, to,
                buckets: Array.from(activeBuckets) as MilestoneBucket[],
                parentId: parentMilestoneId || undefined,
                buildingId: buildingId || undefined,
                limit: PAGE_SIZE,
                offset: nextOffset,
            });
            setTotal(res.total);
            setOffset(nextOffset);
            setRows((prev) => append ? prev.concat(res.milestones) : res.milestones);
        } catch (err: any) {
            setError(err?.message || 'Failed to load milestones');
        } finally {
            setLoading(false);
            setLoadingMore(false);
        }
    }, [projectId, from, to, activeBuckets, parentMilestoneId, buildingId]);

    // Refetch summary whenever the scope/date window changes.
    useEffect(() => {
        fetchSummary();
    }, [fetchSummary]);

    // Refetch the list whenever the filters change.
    useEffect(() => {
        fetchList(0, false);
    }, [fetchList]);

    // Group rows by their parent milestone for a tidy "C Building > C-01st Floor" look.
    const grouped = useMemo(() => {
        const map = new Map<string, { key: string; title: string; subtitle: string; rows: MilestoneBrowseRow[] }>();
        for (const r of rows) {
            const crumbs = r.breadcrumb.map(c => c.name);
            const key = crumbs.length ? crumbs.join(' › ') : '(no parent)';
            const title = crumbs.length ? crumbs[crumbs.length - 1] : 'Top-level';
            const subtitle = crumbs.length > 1 ? crumbs.slice(0, -1).join(' › ') : '';
            if (!map.has(key)) map.set(key, { key, title, subtitle, rows: [] });
            map.get(key)!.rows.push(r);
        }
        return Array.from(map.values());
    }, [rows]);

    const toggleBucket = (b: MilestoneBucket) => {
        setActiveBuckets((prev) => {
            const next = new Set(prev);
            if (next.has(b)) next.delete(b); else next.add(b);
            return next;
        });
    };

    const toggleChecked = (id: string) => {
        setCheckedIds((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id); else next.add(id);
            return next;
        });
    };

    const toggleGroup = (key: string) => {
        setExpandedGroups((prev) => {
            const next = new Set(prev);
            if (next.has(key)) next.delete(key); else next.add(key);
            return next;
        });
    };

    const checkAllInView = () => {
        setCheckedIds(new Set(rows.map(r => r.id)));
    };
    const clearChecked = () => setCheckedIds(new Set());

    const selectedRows = useMemo(
        () => rows.filter(r => checkedIds.has(r.id)),
        [rows, checkedIds]
    );

    const aggregatedLeafIds = useMemo(() => {
        const s = new Set<string>();
        for (const r of selectedRows) for (const id of r.leafTaskIds) s.add(id);
        return Array.from(s);
    }, [selectedRows]);

    // Fire the selection payload up to the host whenever the user touches
    // a checkbox. The host (Planning) hydrates the right-hand task table
    // from this, so there's no explicit "Load" step.
    useEffect(() => {
        onSelectionChange({
            milestoneIds: selectedRows.map(r => r.id),
            leafTaskIds: aggregatedLeafIds,
            milestoneRows: selectedRows,
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [checkedIds]);

    return (
        <div className="flex flex-col h-full">
            {/* Filters */}
            <div className="p-3 border-b border-gray-200 bg-gray-50 space-y-3">
                {scopeLabel && (
                    <div className="px-2 py-1 bg-white border border-blue-200 rounded text-[11px] text-blue-800">
                        <span className="font-medium">Scoped to:</span> {scopeLabel}
                    </div>
                )}
                <div>
                    <div className="text-xs font-medium text-gray-700 mb-1">Schedule window</div>
                    <div className="flex items-center gap-2">
                        <input
                            type="date"
                            value={from}
                            onChange={(e) => setFrom(e.target.value)}
                            className="flex-1 min-w-0 border border-gray-300 rounded px-2 py-1 text-xs bg-white"
                        />
                        <span className="text-gray-400 text-xs">→</span>
                        <input
                            type="date"
                            value={to}
                            onChange={(e) => setTo(e.target.value)}
                            className="flex-1 min-w-0 border border-gray-300 rounded px-2 py-1 text-xs bg-white"
                        />
                    </div>
                    <div className="flex items-center gap-2 mt-1">
                        <button
                            className="text-[11px] text-blue-600 hover:underline"
                            onClick={() => { setFrom(todayOffset(-30)); setTo(todayOffset(90)); }}
                        >
                            -30 / +90d
                        </button>
                        <button
                            className="text-[11px] text-blue-600 hover:underline"
                            onClick={() => { setFrom(todayOffset(-7)); setTo(todayOffset(30)); }}
                        >
                            -7 / +30d
                        </button>
                        <button
                            className="text-[11px] text-blue-600 hover:underline"
                            onClick={() => { setFrom(todayOffset(0)); setTo(todayOffset(180)); }}
                        >
                            next 6mo
                        </button>
                    </div>
                </div>

                {/* Bucket chips with live counts */}
                <div>
                    <div className="text-xs font-medium text-gray-700 mb-1">Status</div>
                    <div className="flex flex-wrap gap-1">
                        {BUCKET_ORDER.map((b) => {
                            const meta = BUCKET_META[b];
                            const Icon = meta.icon;
                            const count = summary?.counts[b] ?? 0;
                            const active = activeBuckets.has(b);
                            return (
                                <button
                                    key={b}
                                    onClick={() => toggleBucket(b)}
                                    className={`inline-flex items-center gap-1 px-2 py-1 rounded-full border text-[11px] font-medium transition ${active ? meta.chipActive : meta.chipClass}`}
                                    title={`${meta.label} (${count})`}
                                >
                                    <Icon className="w-3 h-3" />
                                    <span>{meta.label}</span>
                                    <span className={`inline-flex items-center justify-center min-w-[1.25rem] px-1 rounded-full text-[10px] ${active ? 'bg-white/20' : 'bg-gray-100 text-gray-700'}`}>
                                        {count}
                                    </span>
                                </button>
                            );
                        })}
                    </div>
                </div>
            </div>

            {/* List */}
            <div className="flex-1 overflow-y-auto">
                {loading && rows.length === 0 && (
                    <div className="flex items-center justify-center py-10 text-sm text-gray-500">
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Loading milestones…
                    </div>
                )}

                {error && (
                    <div className="m-3 p-3 border border-red-200 bg-red-50 rounded text-xs text-red-700">
                        {error}
                    </div>
                )}

                {!loading && rows.length === 0 && !error && (
                    <div className="text-center py-10 px-4 text-sm text-gray-500">
                        No milestones match the current filters.
                    </div>
                )}

                {grouped.map((g) => {
                    const expanded = expandedGroups.has(g.key);
                    const allChecked = g.rows.every(r => checkedIds.has(r.id));
                    const someChecked = !allChecked && g.rows.some(r => checkedIds.has(r.id));
                    return (
                        <div key={g.key} className="border-b border-gray-100">
                            <button
                                className="w-full flex items-center gap-2 px-3 py-1.5 bg-gray-50 hover:bg-gray-100 text-left"
                                onClick={() => toggleGroup(g.key)}
                            >
                                {expanded
                                    ? <ChevronDown className="w-3.5 h-3.5 text-gray-500" />
                                    : <ChevronRight className="w-3.5 h-3.5 text-gray-500" />}
                                <input
                                    type="checkbox"
                                    checked={allChecked}
                                    ref={(el) => { if (el) el.indeterminate = someChecked; }}
                                    onChange={(e) => {
                                        e.stopPropagation();
                                        setCheckedIds((prev) => {
                                            const next = new Set(prev);
                                            if (allChecked) {
                                                for (const r of g.rows) next.delete(r.id);
                                            } else {
                                                for (const r of g.rows) next.add(r.id);
                                            }
                                            return next;
                                        });
                                    }}
                                    onClick={(e) => e.stopPropagation()}
                                    className="rounded border-gray-300"
                                />
                                <span className="text-xs font-semibold text-gray-700 truncate flex-1">
                                    {g.title}
                                    {g.subtitle && (
                                        <span className="ml-1 text-[11px] font-normal text-gray-400">
                                            {g.subtitle}
                                        </span>
                                    )}
                                </span>
                                <span className="text-[10px] text-gray-500">{g.rows.length}</span>
                            </button>

                            {expanded && g.rows.map((r) => {
                                const meta = BUCKET_META[r.bucket];
                                const Icon = meta.icon;
                                const checked = checkedIds.has(r.id);
                                return (
                                    <label
                                        key={r.id}
                                        className={`flex items-start gap-2 px-3 py-2 text-xs cursor-pointer hover:bg-blue-50/50 ${checked ? 'bg-blue-50' : ''}`}
                                    >
                                        <input
                                            type="checkbox"
                                            checked={checked}
                                            onChange={() => toggleChecked(r.id)}
                                            className="mt-0.5 rounded border-gray-300"
                                        />
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-start justify-between gap-1">
                                                <div className="font-medium text-gray-900 truncate">
                                                    {r.name}
                                                </div>
                                                <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full border text-[10px] font-medium whitespace-nowrap ${meta.pillClass}`}>
                                                    <Icon className="w-2.5 h-2.5" />
                                                    {meta.label}
                                                </span>
                                            </div>
                                            <div className="mt-0.5 flex items-center gap-2 text-[11px] text-gray-500">
                                                <CalendarDays className="w-3 h-3" />
                                                <span>
                                                    {formatDate(r.plannedStart)} – {formatDate(r.plannedFinish)}
                                                </span>
                                            </div>
                                            <div className="mt-1 flex items-center gap-2">
                                                <div className="flex-1 h-1 bg-gray-200 rounded overflow-hidden">
                                                    <div
                                                        className={`h-full ${r.achieved ? 'bg-emerald-500' : 'bg-blue-500'}`}
                                                        style={{ width: `${Math.min(100, r.progressPercent)}%` }}
                                                    />
                                                </div>
                                                <span className="text-[10px] text-gray-500 w-20 text-right">
                                                    {r.completedCount}/{r.leafCount} done
                                                </span>
                                            </div>
                                        </div>
                                    </label>
                                );
                            })}
                        </div>
                    );
                })}

                {rows.length < total && (
                    <div className="p-3 flex justify-center">
                        <button
                            className="text-xs text-blue-600 hover:underline disabled:opacity-50"
                            disabled={loadingMore}
                            onClick={() => fetchList(offset + PAGE_SIZE, true)}
                        >
                            {loadingMore ? 'Loading…' : `Load more (${total - rows.length} remaining)`}
                        </button>
                    </div>
                )}
            </div>

            {/* Sticky footer — informational only. The right-hand task table
                updates automatically as milestones are checked. */}
            <div className="border-t border-gray-200 bg-white px-3 py-2 flex items-center gap-2">
                <button
                    className="text-xs text-gray-600 hover:underline disabled:opacity-40"
                    onClick={checkedIds.size > 0 ? clearChecked : checkAllInView}
                    disabled={rows.length === 0}
                >
                    {checkedIds.size > 0 ? 'Clear' : 'Select all in view'}
                </button>
                <div className="flex-1 text-[11px] text-gray-500 text-right">
                    {selectedRows.length > 0 ? (
                        <span className="inline-flex items-center gap-1 text-blue-700">
                            <CheckCircle className="w-3 h-3" />
                            {selectedRows.length} milestone{selectedRows.length === 1 ? '' : 's'} · {aggregatedLeafIds.length} task{aggregatedLeafIds.length === 1 ? '' : 's'} loaded
                        </span>
                    ) : (
                        <span>Check a milestone to load its tasks</span>
                    )}
                </div>
            </div>
        </div>
    );
}
